import SEO from "../components/SEO";
import { useState } from "react";
import { Send, Loader2, User, Mail, Phone, Building2, Users, Briefcase } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GetStartedPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    company_name: "",
    title: "",
    num_employees: "",
    email: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/leads`, formData);
      
      if (response.data.success) {
        toast.success("Thank you for your interest!", {
          description: "We'll be in touch soon.",
        });
        navigate("/thank-you");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-50"
      data-testid="get-started-page"
    >
      <SEO title="Get Started" description="Ready to reduce turnover and build more stable teams? Get started with CityWorth's homeownership retention solution." />
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="container-main flex items-center justify-between py-4">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-10" />
          </Link>
          <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium">
            ← Back to Home
          </Link>
        </div>
      </nav>

      <div className="container-main pt-16 pb-20 md:pt-20 md:pb-24">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12 mt-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Let's Start the Conversation
            </h1>
            <p className="text-lg text-slate-600">
              Tell us about your organization and we'll reach out to discuss how CityWorth can help improve employee retention through homeownership benefits.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-slate-200"
          >
            <div className="space-y-6">
              {/* Name Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    First Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="John"
                      required
                      className="pl-12 py-5 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl"
                      data-testid="input-first-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Last Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Smith"
                      required
                      className="pl-12 py-5 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl"
                      data-testid="input-last-name"
                    />
                  </div>
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Company Name *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Acme Corporation"
                    required
                    className="pl-12 py-5 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl"
                    data-testid="input-company-name"
                  />
                </div>
              </div>

              {/* Title & Employees Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Title *
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="HR Director"
                      required
                      className="pl-12 py-5 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl"
                      data-testid="input-title"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Number of Employees *
                  </label>
                  <div className="relative">
                    <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      name="num_employees"
                      value={formData.num_employees}
                      onChange={handleChange}
                      placeholder="500"
                      required
                      className="pl-12 py-5 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl"
                      data-testid="input-num-employees"
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    required
                    className="pl-12 py-5 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl"
                    data-testid="input-email"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone *
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    required
                    className="pl-12 py-5 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary py-6 rounded-xl text-lg font-semibold text-white"
                data-testid="submit-lead-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Get Started
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GetStartedPage;
